// Carrier unlocks (UnlockBase), for the owner alone: the account and its balance, a new unlock
// (IMEI, the service with its price, whatever that service asks for), and every order with its
// status and the code once it's there. Nobody else gets this screen or its menu item, and the
// server refuses every call from any other login. Every order can print the customer's receipt
// (what they pay and that the phone takes 3 to 5 business days to unlock); it prints by itself
// when an order is placed with the customer's price on it.
import { useEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import { ApiError, del, get, patch, post, qs } from '../lib/api';
import { storageGet, storageSet, useLoad } from '../lib/hooks';
import { useSession } from '../lib/session';
import { Badge, Button, Confirm, Cost, Empty, Field, MoneyInput, Profit, Spinner, useAction, useToast } from '../ui/kit';
import { Icon } from '../ui/icons';
import { usePrint } from '../ui/print';
import { useCloudPrint } from '../lib/printing';
import { ReceiptMark } from '../ui/legal';
import { ReceiptHeader, SiteLine, type StoreInfo } from './Receipt';
import { formatDateTime, formatPhone } from '../../shared/format';
import { formatCents } from '../../shared/money';
import { cleanDeviceId, deviceIdProblem } from '../../shared/phone-check';
import {
  OPEN_UNLOCKBASE_STATUSES, UNLOCKBASE_STATUS, creditsLabel, creditsToCents,
  type UnlockbaseAccount, type UnlockbaseOption, type UnlockbaseOrder, type UnlockbaseTool, type UnlockbaseToolList,
} from '../../shared/unlockbase';

const LAST_TOOL = 'sos_unlockbase_tool';
const RECENT_TOOLS = 'sos_unlockbase_recent';

export function UnlocksScreen() {
  const { me } = useSession();
  const owner = me?.employee.role_id === 'owner';
  const account = useLoad<UnlockbaseAccount | null>(() => (owner ? get<UnlockbaseAccount>('/api/unlockbase') : Promise.resolve(null)), [owner]);
  const [ordersVersion, setOrdersVersion] = useState(0);
  const [again, setAgain] = useState<UnlockbaseOrder | null>(null);
  if (!owner) {
    return (
      <div className="page">
        <Empty icon="lock" title="Carrier unlocks are for the owner only.">Only the owner’s login sees this screen and the orders on it.</Empty>
      </div>
    );
  }
  const a = account.data;
  return (
    <div className="page">
      <div className="page-head">
        <h1 style={{ marginRight: 'auto' }}>Carrier unlocks</h1>
        <Badge tone="blue" icon="lock">Owner only</Badge>
      </div>
      {account.error ? <div className="banner warn"><Icon.alert /><div>{account.error.message}</div></div> : null}
      {!a && !account.error ? <Spinner /> : null}
      {a ? <AccountCard account={a} onChange={account.reload} /> : null}
      {a?.connected ? (
        <>
          <NewUnlock account={a} again={again} onOrdered={() => { setOrdersVersion((v) => v + 1); setAgain(null); account.reload(); }} />
          <Orders version={ordersVersion} currency={a.currency} onAgain={(o) => { setAgain(o); window.scrollTo({ top: 0, behavior: 'smooth' }); }} onChanged={account.reload} />
        </>
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------- the account

function AccountCard({ account, onChange }: { account: UnlockbaseAccount; onChange: () => void }) {
  const toast = useToast();
  const { run, busy } = useAction();
  const [key, setKey] = useState('');
  const [showKey, setShowKey] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const connect = async () => {
    const k = key.trim();
    if (!k) { toast('Paste the API key first.', 'error'); return; }
    const r = await run(() => post<UnlockbaseAccount>('/api/unlockbase/key', { key: k }), { success: 'UnlockBase connected.' });
    if (r) { setKey(''); onChange(); }
  };
  const refresh = async () => {
    const r = await run(() => get<UnlockbaseAccount>('/api/unlockbase/account'));
    if (r?.error) toast(r.error, 'error');
    onChange();
  };
  if (!account.connected) {
    return (
      <div className="card stack">
        <h2>Connect UnlockBase</h2>
        <p className="small muted">
          Paste the XML API key from your UnlockBase account (UnlockBase → API). STORE OS checks it with UnlockBase, keeps it on the server and never shows it again; it isn’t in backups or the audit log.
          Orders are paid from the credits on your UnlockBase account.
        </p>
        <div className="row-wrap">
          {/* A plain text box, not a password box: the browser must never fill a saved STORE OS password in here. Masked unless "Show" is ticked. */}
          <input
            className="input" type="text" name="unlockbase-api-key" autoComplete="off" autoCapitalize="off" autoCorrect="off" spellCheck={false} data-lpignore="true" data-1p-ignore="true"
            value={key} onChange={(e) => setKey(e.target.value)} placeholder="UnlockBase API key" aria-label="UnlockBase API key"
            style={{ flex: '1 1 320px', ...(showKey ? {} : { WebkitTextSecurity: 'disc' }) } as CSSProperties} onKeyDown={(e) => { if (e.key === 'Enter') connect(); }}
          />
          <label className="check"><input type="checkbox" checked={showKey} onChange={(e) => setShowKey(e.target.checked)} /><span>Show</span></label>
          <Button variant="primary" busy={busy} onClick={connect}>Connect</Button>
        </div>
      </div>
    );
  }
  return (
    <div className="card stack">
      <div className="spread">
        <h2>UnlockBase</h2>
        <Badge tone="green" icon="check">Connected</Badge>
      </div>
      <div className="row-wrap">
        <div className="ub-balance">
          <span className="small muted">Credits</span>
          <span className="ub-balance-v num">{account.credits != null ? creditsLabel(account.credits, account.currency) : '—'}</span>
          <span className="small muted">{account.email ? `${account.email} · ` : ''}{account.checked_at ? `as of ${formatDateTime(account.checked_at)}` : 'not read yet'}</span>
        </div>
        <div className="ub-balance">
          <span className="small muted">Open orders</span>
          <span className="ub-balance-v num">{account.open}</span>
          <span className="small muted">STORE OS asks UnlockBase every few minutes</span>
        </div>
        <div className="row-wrap" style={{ marginLeft: 'auto' }}>
          <Button size="sm" icon="refresh" busy={busy} onClick={refresh}>Refresh balance</Button>
          <Button size="sm" variant="ghost" onClick={() => setDisconnecting(true)}>Disconnect</Button>
        </div>
      </div>
      {account.error ? <div className="banner warn"><Icon.alert /><div>{account.error}</div></div> : null}
      {disconnecting ? (
        <Confirm title="Disconnect UnlockBase?" confirmLabel="Disconnect" danger busy={busy} onClose={() => setDisconnecting(false)} onConfirm={async () => {
          const r = await run(() => del('/api/unlockbase/key'), { success: 'UnlockBase disconnected.' });
          if (r) { setDisconnecting(false); onChange(); }
        }}>
          <p>The API key is removed from STORE OS. Orders already placed stay listed, but STORE OS stops asking UnlockBase about them until you connect again.</p>
        </Confirm>
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------- a new unlock

interface Options { tool: UnlockbaseTool; networks: UnlockbaseOption[] | null; mobiles: UnlockbaseOption[] | null }

function NewUnlock({ account, again, onOrdered }: { account: UnlockbaseAccount; again: UnlockbaseOrder | null; onOrdered: () => void }) {
  const toast = useToast();
  const { run, busy } = useAction();
  const [fresh, setFresh] = useState(0);
  const list = useLoad<UnlockbaseToolList>(() => get(`/api/unlockbase/tools${qs({ fresh: fresh ? 1 : undefined })}`), [fresh]);
  const [imei, setImei] = useState('');
  const [q, setQ] = useState('');
  const [group, setGroup] = useState('');
  const [toolId, setToolId] = useState(() => storageGet<string>(LAST_TOOL, ''));
  const [values, setValues] = useState<Record<string, string>>({});
  const [email, setEmail] = useState('');
  const [comments, setComments] = useState('');
  // What the customer pays for the unlock (optional; it can be set on the order later).
  const [charge, setCharge] = useState<number | null>(null);
  const [confirm, setConfirm] = useState<string | null>(null);
  const [dupe, setDupe] = useState<UnlockbaseOrder | null>(null);
  const [pending, setPending] = useState<string | null>(null);
  const imeiRef = useRef<HTMLInputElement>(null);
  const printReceipt = useUnlockReceiptPrint();
  const options = useLoad<Options | null>(() => (toolId ? get<Options>(`/api/unlockbase/tools/${encodeURIComponent(toolId)}`) : Promise.resolve(null)), [toolId]);
  // "Order again" from an order below: the same IMEI and service.
  useEffect(() => {
    if (!again) return;
    setImei(again.imei);
    setToolId(again.tool_id);
    setComments(again.comments ?? '');
    setEmail(again.customer_email ?? '');
    setCharge(again.charge_cents);
    imeiRef.current?.focus();
  }, [again]);
  const tools = list.data?.tools ?? [];
  const tool = tools.find((t) => t.id === toolId) ?? null;
  const recent = useMemo(() => storageGet<string[]>(RECENT_TOOLS, []), [list.data]);
  const words = q.trim().toLowerCase().split(/\s+/).filter(Boolean);
  const shown = useMemo(() => {
    const matches = tools.filter((t) => (!group || t.group === group) && words.every((w) => t.name.toLowerCase().includes(w) || t.group.toLowerCase().includes(w)));
    // The services used lately first, then the US carriers (UnlockBase lists every country), then by group and name.
    const us = (t: UnlockbaseTool) => (/\b(USA|U\.S\.A?\.?|United States|AT&T|T-Mobile|Verizon|Sprint|Cricket|Metro|Boost|Xfinity|Spectrum|US Cellular)\b/i.test(`${t.group} ${t.name}`) ? 0 : 1);
    return matches.slice().sort((x, y) => (recent.indexOf(x.id) === -1 ? 1 : 0) - (recent.indexOf(y.id) === -1 ? 1 : 0) || us(x) - us(y) || x.group.localeCompare(y.group) || x.name.localeCompare(y.name)).slice(0, 200);
  }, [tools, group, words.join(' '), recent]);
  const id = cleanDeviceId(imei);
  const imeiProblem = id ? (/^\d{15}$/.test(id) ? deviceIdProblem(id) : 'UnlockBase needs the 15-digit IMEI (dial *#06#).') : null;
  const pick = (t: UnlockbaseTool) => {
    setToolId(t.id);
    setValues({});
    storageSet(LAST_TOOL, t.id);
  };
  const set = (k: string, v: string) => setValues((x) => ({ ...x, [k]: v }));
  const place = async (requestId: string, orderAgain: boolean) => {
    const r = await run(async () => {
      try {
        return await post<UnlockbaseOrder>('/api/unlockbase/orders', { imei: id, tool: toolId, email: email || null, comments: comments || null, ...values, chargeCents: charge, again: orderAgain, requestId });
      } catch (e) {
        const err = e instanceof ApiError ? e : null;
        if (err && err.status === 409 && err.details?.code === 'already_ordered') { setDupe(err.details.order as UnlockbaseOrder); setPending(requestId); return undefined; }
        throw e;
      }
    });
    if (!r) return;
    setConfirm(null);
    setDupe(null);
    setPending(null);
    if (r.status === 'failed') toast(r.error ?? 'UnlockBase didn’t take the order.', 'error');
    else if (r.status === 'done') toast(`Done: the code is below.`, 'good');
    else if (r.status === 'check') toast(r.error ?? 'Look at your orders on UnlockBase.', 'error');
    else toast(`Ordered on UnlockBase (#${r.ub_id}). The code shows below when it’s ready.`, 'good');
    // The customer's receipt prints by itself when the order went through with their price on it.
    if (r.status !== 'failed' && r.charge_cents !== null) void printReceipt(r);
    storageSet(RECENT_TOOLS, [toolId, ...recent.filter((x) => x !== toolId)].slice(0, 8));
    setImei('');
    setComments('');
    setEmail('');
    setCharge(null);
    setValues({});
    onOrdered();
    imeiRef.current?.focus();
  };
  const ready = !!tool && !!id && !imeiProblem && !options.loading && (tool.requires.network !== 'required' || !!values.network) && (tool.requires.mobile !== 'required' || !!values.mobile)
    && (tool.requires.provider !== 'required' || !!values.provider) && (tool.requires.pin !== 'required' || !!values.pin) && (tool.requires.type !== 'required' || !!values.type)
    && (tool.requires.kbh !== 'required' || !!values.kbh) && (tool.requires.mep !== 'required' || !!values.mep) && (tool.requires.prd !== 'required' || !!values.prd) && (tool.requires.locks === 0 || !!values.locks);
  const price = tool ? creditsLabel(tool.credits, account.currency) : '';
  // UnlockBase's price in cents (the cost), for the profit next to what the customer pays.
  const cost = tool ? creditsToCents(tool.credits, account.currency) : null;
  const textField = (key: 'provider' | 'pin' | 'type' | 'kbh' | 'mep' | 'prd', label: string, hint: string, placeholder = '') => {
    const need = tool?.requires[key] ?? 'none';
    if (need === 'none') return null;
    return (
      <Field key={key} label={label} hint={need === 'required' ? hint : `${hint} · optional`}>
        <input className="input" value={values[key] ?? ''} onChange={(e) => set(key, e.target.value)} placeholder={placeholder} aria-label={label} />
      </Field>
    );
  };
  return (
    <div className="card stack">
      <div className="spread">
        <h2>New unlock</h2>
        {list.data?.fetched_at ? <span className="small muted">Prices as of {formatDateTime(list.data.fetched_at)}{list.data.stale ? ' (UnlockBase couldn’t be asked again)' : ''}</span> : null}
      </div>
      <div className="form-grid">
        <Field label="IMEI" hint="15 digits · dial *#06# on the phone">
          <input ref={imeiRef} className="input" inputMode="numeric" value={imei} onChange={(e) => setImei(e.target.value)} placeholder="35xxxxxxxxxxxxx" aria-label="IMEI" autoFocus />
        </Field>
        <Field label="Customer’s email" hint="optional · UnlockBase emails them the code too">
          <input className="input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="name@example.com" aria-label="Customer’s email" />
        </Field>
        <Field label="Note" hint="optional · the customer, the ticket">
          <input className="input" value={comments} onChange={(e) => setComments(e.target.value)} placeholder="Maria · repair #1042" aria-label="Note" maxLength={200} />
        </Field>
        <Field label="Customer pays" hint={charge !== null && cost !== null ? <span className="pill-row"><Cost cents={cost} /><Profit cents={charge - cost} /></span> : 'optional · what you charge for the unlock; the profit shows next to it'}>
          <MoneyInput value={charge} onChange={setCharge} ariaLabel="Customer pays" />
        </Field>
      </div>
      {imeiProblem && id.length >= 10 ? <p className="small" role="alert" style={{ color: 'var(--red)' }}>{imeiProblem}</p> : null}

      <div className="stack-sm">
        <div className="spread">
          <span className="strong">Service</span>
          <Button size="sm" variant="ghost" icon="refresh" busy={list.loading} onClick={() => setFresh((n) => n + 1)}>Reload prices</Button>
        </div>
        {list.error ? <div className="banner warn"><Icon.alert /><div>{list.error.message}</div></div> : null}
        {list.data?.error && list.data.stale ? <div className="banner warn"><Icon.alert /><div>{list.data.error}</div></div> : null}
        {!list.data && !list.error ? <Spinner /> : null}
        {list.data ? (
          <>
            <div className="row-wrap">
              <input className="input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search: AT&T iPhone, T-Mobile, Samsung…" aria-label="Search services" style={{ flex: '1 1 260px' }} />
              <select className="input" value={group} onChange={(e) => setGroup(e.target.value)} aria-label="Group" style={{ flex: '0 1 260px' }}>
                <option value="">Every group ({list.data.groups.length})</option>
                {list.data.groups.map((g) => <option key={g} value={g}>{g}</option>)}
              </select>
            </div>
            <div className="ub-tools" role="listbox" aria-label="UnlockBase services">
              {shown.map((t) => (
                <button key={t.id} type="button" role="option" aria-selected={t.id === toolId} className={`ub-tool${t.id === toolId ? ' ub-tool-on' : ''}`} onClick={() => pick(t)}>
                  <span className="ub-tool-main">
                    <span className="ub-tool-n">{t.name}</span>
                    <span className="small muted">{t.group}{t.delivery ? ` · ${t.delivery}` : ''}{recent.includes(t.id) ? ' · used lately' : ''}</span>
                  </span>
                  <span className="ub-tool-p num">{creditsLabel(t.credits, account.currency)}</span>
                </button>
              ))}
              {!shown.length ? <p className="small muted" style={{ padding: 12 }}>No service matches. Try fewer words, or another group.</p> : null}
              {shown.length === 200 ? <p className="small muted" style={{ padding: 12 }}>The first 200 are shown; search to narrow it down.</p> : null}
            </div>
          </>
        ) : null}
      </div>

      {tool ? (
        <div className="ub-picked stack-sm">
          <div className="spread">
            <div>
              <span className="strong">{tool.name}</span>
              <span className="small muted"> · {tool.group}{tool.delivery ? ` · ${tool.delivery}` : ''}</span>
            </div>
            <span className="strong num">{price}</span>
          </div>
          {tool.message ? <p className="small muted" style={{ whiteSpace: 'pre-line' }}>{tool.message}</p> : null}
          {options.loading ? <Spinner label="Asking UnlockBase what this service needs" /> : null}
          {options.error ? <div className="banner warn"><Icon.alert /><div>{options.error.message}</div></div> : null}
          {options.data && (options.data.networks || options.data.mobiles || tool.requires.provider !== 'none' || tool.requires.pin !== 'none' || tool.requires.type !== 'none' || tool.requires.kbh !== 'none' || tool.requires.mep !== 'none' || tool.requires.prd !== 'none' || tool.requires.locks > 0) ? (
            <div className="form-grid">
              {options.data.networks ? (
                <Field label="Network (the carrier it’s locked to)" hint={tool.requires.network === 'required' ? 'needed' : 'optional'}>
                  <select className="input" value={values.network ?? ''} onChange={(e) => set('network', e.target.value)} aria-label="Network">
                    <option value="">{tool.requires.network === 'required' ? 'Pick the network…' : 'Not needed'}</option>
                    {options.data.networks.map((n) => <option key={n.id} value={n.id}>{n.name}</option>)}
                  </select>
                </Field>
              ) : null}
              {options.data.mobiles ? (
                <Field label="Phone model" hint={tool.requires.mobile === 'required' ? 'needed' : 'optional'}>
                  <select className="input" value={values.mobile ?? ''} onChange={(e) => set('mobile', e.target.value)} aria-label="Phone model">
                    <option value="">{tool.requires.mobile === 'required' ? 'Pick the model…' : 'Not needed'}</option>
                    {options.data.mobiles.map((n) => <option key={n.id} value={n.id}>{n.name}</option>)}
                  </select>
                </Field>
              ) : null}
              {textField('provider', 'Provider ID', '13 characters, on Alcatel phones', 'XXXXX-XXXXXXX')}
              {textField('pin', 'BlackBerry PIN', '8 letters and digits')}
              {textField('type', 'Phone type', 'from *#0000#')}
              {textField('kbh', 'KBH', 'from the phone')}
              {textField('mep', 'MEP', 'MEP-XXXXX-XXX', 'MEP-00000-000')}
              {textField('prd', 'PRD', 'PRD-XXXXX-XXX', 'PRD-00000-000')}
              {tool.requires.locks > 0 ? (
                <Field label="Lock codes to ask for" hint={`${tool.requires.locks} of NCK, NSCK, SPK, CCK, ESL · comma-separated`}>
                  <input className="input" value={values.locks ?? ''} onChange={(e) => set('locks', e.target.value)} placeholder={tool.requires.locks === 1 ? 'NCK' : 'NCK, NSCK'} aria-label="Lock codes" />
                </Field>
              ) : null}
            </div>
          ) : null}
        </div>
      ) : null}

      <div className="row-wrap">
        <Button variant="primary" size="lg" icon="unlock" disabled={!ready} busy={busy} onClick={() => setConfirm(`tap-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`)}>
          Order{tool ? ` · ${price}` : ''}
        </Button>
        <span className="small muted">Paid from the credits on your UnlockBase account. The code shows below when it’s ready.</span>
      </div>

      {confirm && tool && !dupe ? (
        <Confirm title="Order this unlock?" confirmLabel={`Order · ${price}`} busy={busy} onClose={() => setConfirm(null)} onConfirm={() => place(confirm, false)}>
          <p><strong>{tool.name}</strong> for IMEI <span className="num">{id}</span>{values.network && options.data?.networks ? ` · ${options.data.networks.find((n) => n.id === values.network)?.name ?? ''}` : ''}{values.mobile && options.data?.mobiles ? ` · ${options.data.mobiles.find((n) => n.id === values.mobile)?.name ?? ''}` : ''}.</p>
          <p className="small muted">{price} comes off your UnlockBase credits{tool.delivery ? `; delivery ${tool.delivery.toLowerCase()}` : ''}. UnlockBase doesn’t refund an unlock it delivers.</p>
        </Confirm>
      ) : null}
      {dupe ? (
        <Confirm title="Already ordered" confirmLabel="Order it again" danger busy={busy} onClose={() => { setDupe(null); setPending(null); setConfirm(null); }} onConfirm={() => place(`${pending ?? confirm ?? 'tap'}-again`, true)}>
          <p>This IMEI already has <strong>{dupe.tool_name}</strong> · {dupe.status_label}{dupe.ub_id ? ` (UnlockBase #${dupe.ub_id})` : ''} · {formatDateTime(dupe.created_at)}.</p>
          {dupe.code ? <p>Its code: <span className="num strong">{dupe.code}</span></p> : null}
          <p className="small muted">Ordering again charges {price} again.</p>
        </Confirm>
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------- the orders

function Orders({ version, currency, onAgain, onChanged }: { version: number; currency: string | null; onAgain: (o: UnlockbaseOrder) => void; onChanged: () => void }) {
  const [filter, setFilter] = useState('');
  const fid = cleanDeviceId(filter);
  const orders = useLoad<UnlockbaseOrder[]>(() => get(`/api/unlockbase/orders${qs({ imei: fid.length >= 15 ? fid : undefined, limit: 100 })}`), [version, fid.length >= 15 ? fid : '']);
  const reload = orders.reload;
  const open = (orders.data ?? []).some((o) => OPEN_UNLOCKBASE_STATUSES.includes(o.status) || o.status === 'sending');
  // While an order is open, look again every minute (STORE OS asks UnlockBase every few minutes).
  useEffect(() => {
    if (!open) return undefined;
    const t = setInterval(reload, 60_000);
    return () => clearInterval(t);
  }, [open, reload]);
  return (
    <div className="card stack">
      <div className="spread">
        <h2>Orders</h2>
        <input className="input" value={filter} onChange={(e) => setFilter(e.target.value)} placeholder="Find by IMEI" aria-label="Find orders by IMEI" inputMode="numeric" style={{ maxWidth: 260 }} />
      </div>
      {orders.error ? <div className="banner warn"><Icon.alert /><div>{orders.error.message}</div></div> : null}
      {!orders.data && !orders.error ? <Spinner /> : null}
      {orders.data && !orders.data.length ? <p className="small muted">{fid.length >= 15 ? 'No orders for that IMEI.' : 'No unlocks ordered yet. The first one shows here with its status and, when it’s ready, the code.'}</p> : null}
      {orders.data?.length ? (
        <div className="ub-orders">
          {orders.data.map((o) => <OrderCard key={o.id} order={o} currency={currency} onChanged={() => { reload(); onChanged(); }} onAgain={onAgain} />)}
        </div>
      ) : null}
    </div>
  );
}

/** What the unlock cost (UnlockBase's price, red), what the customer pays, and the profit (green), with a way to set or change the price. */
function ChargeLine({ o, currency, onChanged }: { o: UnlockbaseOrder; currency: string | null; onChanged: () => void }) {
  const { run, busy } = useAction();
  const [editing, setEditing] = useState(false);
  const [charge, setCharge] = useState<number | null>(o.charge_cents);
  useEffect(() => setCharge(o.charge_cents), [o.charge_cents]);
  const off = o.status === 'rejected' || o.status === 'canceled' || o.status === 'failed';
  const save = async () => {
    const r = await run(() => patch<UnlockbaseOrder>(`/api/unlockbase/orders/${o.id}`, { chargeCents: charge }), { success: 'Saved.' });
    if (r) { setEditing(false); onChanged(); }
  };
  if (editing) {
    return (
      <div className="row-wrap" style={{ gap: 8 }}>
        <span className="small strong">Customer pays</span>
        <MoneyInput value={charge} onChange={setCharge} ariaLabel="Customer pays" />
        <Button size="sm" variant="primary" busy={busy} disabled={charge === o.charge_cents} onClick={save}>Save</Button>
        <Button size="sm" variant="ghost" onClick={() => { setEditing(false); setCharge(o.charge_cents); }}>Cancel</Button>
      </div>
    );
  }
  return (
    <div className="pill-row small">
      {o.cost_cents !== null ? <Cost cents={o.cost_cents} label={off ? 'Cost · credits back' : 'UnlockBase'} /> : <span className="muted">UnlockBase {creditsLabel(o.credits, currency) || '—'}</span>}
      {o.charge_cents !== null ? <span className="strong">Customer pays {formatCents(o.charge_cents)}</span> : null}
      {o.profit_cents !== null ? <Profit cents={o.profit_cents} /> : null}
      <button type="button" className="link-btn" onClick={() => setEditing(true)}>{o.charge_cents === null ? 'Set what the customer pays' : 'Change'}</button>
    </div>
  );
}

function OrderCard({ order: o, currency, onChanged, onAgain }: { order: UnlockbaseOrder; currency: string | null; onChanged: () => void; onAgain: (o: UnlockbaseOrder) => void }) {
  const toast = useToast();
  const { run, busy } = useAction();
  const [canceling, setCanceling] = useState(false);
  const printReceipt = useUnlockReceiptPrint();
  const st = UNLOCKBASE_STATUS[o.status];
  const check = async () => {
    const r = await run(() => post<UnlockbaseOrder>(`/api/unlockbase/orders/${o.id}/check`));
    if (r) {
      if (r.status === 'done' && o.status !== 'done') toast('The code is here.', 'good');
      else if (r.status === o.status) toast(r.status === 'waiting' ? 'Still waiting at UnlockBase.' : r.status_label, 'info');
      onChanged();
    }
  };
  const copy = async () => {
    if (!o.code) return;
    try {
      await navigator.clipboard.writeText(o.code);
      toast('Code copied.', 'good');
    } catch {
      toast('Couldn’t copy. Select the code and copy it.', 'error');
    }
  };
  return (
    <div className={`ub-order ub-order-${o.status}`}>
      <div className="ub-order-head">
        <div className="ub-order-title">
          <span className="strong">{o.tool_name}</span>
          <span className="small muted">{o.group_name ? `${o.group_name} · ` : ''}IMEI <span className="num">{o.imei}</span>{o.network_name ? ` · ${o.network_name}` : ''}{o.mobile_name ? ` · ${o.mobile_name}` : ''}</span>
        </div>
        <Badge tone={st.tone}>{st.label}</Badge>
      </div>
      {o.code ? (
        <div className="ub-code">
          <pre className="ub-code-v num">{o.code}</pre>
          <Button size="sm" onClick={copy}>Copy code</Button>
        </div>
      ) : null}
      {o.error ? <p className={`small ${o.status === 'failed' || o.status === 'rejected' || o.status === 'check' ? '' : 'muted'}`} style={o.status === 'failed' || o.status === 'rejected' || o.status === 'check' ? { color: 'var(--red)' } : undefined}>{o.error}</p> : null}
      <ChargeLine o={o} currency={currency} onChanged={onChanged} />
      <div className="ub-order-foot small muted">
        <span>{formatDateTime(o.created_at)}{o.employee_name ? ` · ${o.employee_name}` : ''}{o.ub_id ? ` · UnlockBase #${o.ub_id}` : ''}{o.credits ? ` · ${creditsLabel(o.credits, currency)}` : ''}</span>
        {o.comments ? <span>· {o.comments}</span> : null}
        {o.customer_email ? <span>· {o.customer_email}</span> : null}
        {o.delivered_at ? <span>· delivered {formatDateTime(o.delivered_at)}</span> : o.checked_at ? <span>· checked {formatDateTime(o.checked_at)}</span> : null}
        <span className="row-wrap" style={{ marginLeft: 'auto' }}>
          {o.status !== 'failed' ? <Button size="sm" variant="ghost" icon="printer" onClick={() => printReceipt(o)}>Customer receipt</Button> : null}
          {o.status === 'waiting' || o.status === 'check' ? <Button size="sm" icon="refresh" busy={busy} onClick={check}>Check now</Button> : null}
          {o.status === 'waiting' && o.ub_id ? <Button size="sm" variant="ghost" onClick={() => setCanceling(true)}>Cancel</Button> : null}
          {o.status === 'rejected' || o.status === 'failed' || o.status === 'canceled' ? <Button size="sm" variant="ghost" onClick={() => onAgain(o)}>Order again</Button> : null}
        </span>
      </div>
      {canceling ? (
        <Confirm title="Cancel this order?" confirmLabel="Cancel the order" danger busy={busy} onClose={() => setCanceling(false)} onConfirm={async () => {
          const r = await run(() => post<UnlockbaseOrder>(`/api/unlockbase/orders/${o.id}/cancel`));
          if (r) {
            setCanceling(false);
            toast(r.status === 'canceled' ? 'Canceled; the credits are back.' : (r.error ?? 'Cancellation requested.'), r.status === 'canceled' ? 'good' : 'info');
            onChanged();
          }
        }}>
          <p>UnlockBase cancels an order right away in its first 15 minutes. After that it’s a request its provider has to approve, and the order stays open until they do.</p>
        </Confirm>
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------- the customer's receipt

/** What GET /api/unlockbase/orders/:id/receipt returns: the store, the order, what the customer pays, and the wait. */
interface UnlockReceipt {
  business_name: string; logo_url: string; website: string; store: StoreInfo; timezone: string;
  number: string; created_at: number; employee: string | null;
  imei: string; service: string; group: string | null; network: string | null; mobile: string | null;
  customer_email: string | null; status: string; status_label: string;
  /** What the customer pays; null when it wasn't set (the receipt then shows no price). */
  charge_cents: number | null;
  /** "3 to 5 business days". */
  wait: string;
}

/**
 * The customer's carrier unlock receipt: the service and the IMEI, what they pay, and that the
 * phone takes 3 to 5 business days to unlock. Nothing about what the unlock cost the store.
 */
export function UnlockReceiptPaper({ r }: { r: UnlockReceipt }) {
  return (
    <div className="receipt">
      <ReceiptHeader logo={r.logo_url} business={r.business_name} store={r.store} website={r.website} />
      <div className="order-banner">CARRIER UNLOCK</div>
      <div className="c strong">{r.service}</div>
      {r.group ? <div className="c dim">{r.group}</div> : null}
      <hr />
      <dl className="meta">
        <dt>Ordered</dt><dd>{formatDateTime(r.created_at)}</dd>
        <dt>Order</dt><dd>{r.number}</dd>
        <dt>IMEI</dt><dd className="strong num">{r.imei}</dd>
        {r.network ? <><dt>Network</dt><dd>{r.network}</dd></> : null}
        {r.mobile ? <><dt>Phone</dt><dd>{r.mobile}</dd></> : null}
        {r.customer_email ? <><dt>Email</dt><dd>{r.customer_email}</dd></> : null}
        {r.employee ? <><dt>Served by</dt><dd>{r.employee}</dd></> : null}
      </dl>
      {r.charge_cents !== null ? (
        <>
          <hr />
          <div className="rl"><span>Carrier unlock</span><span>{formatCents(r.charge_cents)}</span></div>
          <hr />
          <div className="rl tot"><span>TOTAL</span><span>{formatCents(r.charge_cents)}</span></div>
        </>
      ) : null}
      <hr />
      <div className="c strong big-dev">Please allow {r.wait} for the phone to unlock.</div>
      <div className="c dim pickup-note">We’ll let you know as soon as it’s done. Keep this receipt.</div>
      {r.store.phone ? <div className="c dim">Questions? {formatPhone(r.store.phone)}</div> : null}
      <SiteLine website={r.website} />
      <ReceiptMark />
    </div>
  );
}

/** Prints an unlock order's customer receipt on the store's receipt printer, or from this browser. */
function useUnlockReceiptPrint() {
  const print = usePrint();
  const cloud = useCloudPrint();
  const toast = useToast();
  return (o: UnlockbaseOrder) => cloud.printOut({
    kind: 'unlock_receipt', refId: o.id, title: `Unlock ${o.ub_id ? `#${o.ub_id}` : o.imei} receipt`,
    browser: () => {
      get<UnlockReceipt>(`/api/unlockbase/orders/${o.id}/receipt`)
        .then((r) => print(<UnlockReceiptPaper r={r} />, 'thermal'))
        .catch((e) => toast(e instanceof Error ? e.message : 'The receipt couldn’t print.', 'error'));
    },
  });
}
